/**
 * Model Catalog (Track B-4 + #3292)
 *
 * Read-only view of the unified model catalog served by
 * GET /v1/admin/models/catalog — every modality the deployment serves, with
 * dual descriptors (billing + capability) and live dora editorial overlay.
 */

import { useMemo, useState } from 'react';

import {
  CapabilityWarningBadge,
  ModelBadge,
  ModelCapabilitySummary,
  SourceHealthBanner,
  type SourceStatus,
} from '@/components/model-display';
import type { CatalogModel, Modality, ModelCatalogResponse } from '@/types';
import { useModelCatalog } from '@/lib/queries/useModelsQuery';
import { formatBilling, modalityLabel } from './catalogFormat';

const MODALITIES: Modality[] = ['text', 'image', 'tts', 'stt', 'embedding', 'rerank'];

interface ModelCatalogPanelProps {
  initialModality?: Modality | 'all';
}

type CatalogWithTrustSignals = ModelCatalogResponse & {
  sources?: SourceStatus[];
};

function capabilityInconsistent(model: CatalogModel): boolean {
  return 'capability_inconsistent' in model && model.capability_inconsistent === true;
}

function conflictFlags(model: CatalogModel): string[] {
  const flags = model.capability_conflict_flags;
  return Array.isArray(flags) ? flags.filter((f): f is string => typeof f === 'string' && f.length > 0) : [];
}

export function ModelCatalogPanel({ initialModality = 'all' }: ModelCatalogPanelProps) {
  const { data, isLoading, error } = useModelCatalog();
  const [modality, setModality] = useState<Modality | 'all'>(initialModality);
  const [inconsistentOnly, setInconsistentOnly] = useState(false);
  const sources = (data as CatalogWithTrustSignals | undefined)?.sources;

  const models = useMemo(() => {
    const all = data?.models ?? [];
    return all.filter((m) => {
      if (modality !== 'all' && m.capability.modality !== modality) return false;
      if (inconsistentOnly && !capabilityInconsistent(m)) return false;
      return true;
    });
  }, [data, modality, inconsistentOnly]);

  const inconsistentCount = useMemo(
    () => (data?.models ?? []).filter(capabilityInconsistent).length,
    [data],
  );

  if (isLoading) {
    return (
      <div data-testid="catalog-loading" className="p-6 text-sm text-gray-500">
        Loading model catalog…
      </div>
    );
  }
  if (error) {
    return (
      <div data-testid="catalog-error" className="p-6 text-sm text-red-600">
        Failed to load model catalog: {error.message}
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      <SourceHealthBanner sources={sources} className="rounded-lg border" />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Model Catalog</h2>
          <p className="mt-0.5 text-xs text-gray-500">
            统一目录：计费 · 能力 · 路由档 · 多通道信任标记（first-wins）
          </p>
        </div>
        <span className="text-sm text-gray-500">
          <span data-testid="catalog-count">{models.length}</span> shown ·{' '}
          <span data-testid="catalog-total">{data?.count ?? data?.models?.length ?? models.length}</span>{' '}
          total
          {inconsistentCount > 0 && (
            <span className="ml-2 text-amber-700" data-testid="catalog-inconsistent-count">
              · {inconsistentCount} 能力不一致
            </span>
          )}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {(['all', ...MODALITIES] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setModality(m)}
            className={`rounded px-2 py-1 text-xs ${
              modality === m ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
            }`}
          >
            {m === 'all' ? 'All' : modalityLabel(m)}
          </button>
        ))}
        <button
          type="button"
          data-testid="catalog-filter-inconsistent"
          onClick={() => setInconsistentOnly((v) => !v)}
          className={`rounded border px-2 py-1 text-xs ${
            inconsistentOnly
              ? 'border-amber-500 bg-amber-50 text-amber-900'
              : 'border-transparent bg-gray-100 text-gray-700'
          }`}
        >
          仅能力不一致
        </button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <th className="px-3 py-2.5 pr-4 font-medium">Model</th>
              <th className="px-3 py-2.5 pr-4 font-medium">Capability</th>
              <th className="px-3 py-2.5 pr-4 font-medium">Billing</th>
              <th className="px-3 py-2.5 pr-4 font-medium">Tier / Quality</th>
              <th className="px-3 py-2.5 pr-4 font-medium">Tags</th>
            </tr>
          </thead>
          <tbody>
            {models.map((model: CatalogModel) => {
              const inconsistent = capabilityInconsistent(model);
              const flags = conflictFlags(model);
              const title = model.display_name?.trim() || model.id;
              return (
                <tr
                  key={model.id}
                  data-testid={`catalog-row-${model.id}`}
                  className={`border-b align-top last:border-b-0 ${
                    inconsistent ? 'bg-amber-50/40' : 'bg-white'
                  }`}
                >
                  <td className="px-3 py-3 pr-4">
                    <div className="flex flex-col gap-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="font-medium text-gray-900">{title}</span>
                        <CapabilityWarningBadge
                          inconsistent={inconsistent}
                          conflictFlags={flags}
                          showFlagList
                          className="font-sans"
                        />
                      </div>
                      {title !== model.id && (
                        <span className="font-mono text-[11px] text-gray-500">{model.id}</span>
                      )}
                      {model.description ? (
                        <p className="line-clamp-2 max-w-md text-xs leading-snug text-gray-600">
                          {model.description}
                        </p>
                      ) : null}
                      {(model.provider || model.organization) && (
                        <span className="text-[11px] text-gray-400">
                          {[model.provider, model.organization].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-3 pr-4">
                    <div className="flex flex-col gap-1.5">
                      <span className="text-[11px] text-gray-400">
                        {modalityLabel(model.capability.modality)}
                      </span>
                      <ModelCapabilitySummary capability={model.capability} />
                    </div>
                  </td>
                  <td className="px-3 py-3 pr-4">
                    <div className="max-w-[14rem] text-xs leading-snug text-gray-800">
                      {formatBilling(model.billing)}
                    </div>
                    {model.discount_vs_standard != null && model.discount_vs_standard > 0 && (
                      <span className="mt-1 inline-block text-[11px] text-emerald-700">
                        −{Math.round(model.discount_vs_standard * 100)}% vs standard
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3 pr-4">
                    <div className="flex flex-col gap-1">
                      <span className="text-xs text-gray-700">{model.routing_tier ?? '—'}</span>
                      <ModelBadge model={model} />
                    </div>
                  </td>
                  <td className="px-3 py-3 pr-4">
                    {(model.tags ?? []).length > 0 ? (
                      <div className="flex max-w-[16rem] flex-wrap gap-1">
                        {(model.tags ?? []).map((tag) => (
                          <span
                            key={tag}
                            className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-700"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ModelCatalogPanel;
